import { Fragment, useMemo, useSyncExternalStore, type ReactNode } from 'react';
import type { Locale, MessageParams } from '@aitoken/contracts';
import { getAvailableLocales, getLocale, subscribe, translate } from './runtime';

export { LOCALE_LABELS, LOCALES, type Locale } from '@aitoken/contracts';
export { getLocale, getLocaleTag, setLocale, translate, type Translation } from './runtime';

export function useLocale(): Locale {
  return useSyncExternalStore(subscribe, getLocale, getLocale);
}

/** 本站提供的语言（国内站只有简体中文） */
export function useAvailableLocales(): readonly Locale[] {
  return useSyncExternalStore(subscribe, getAvailableLocales, getAvailableLocales);
}

type RichTags = Record<string, (chunks: ReactNode) => ReactNode>;

export interface TFunction<K extends string> {
  (key: K, params?: MessageParams): string;
  /** 文案中的 <tag>…</tag> 交给对应函数渲染，例如 {link: (c) => <Link to="/docs">{c}</Link>} */
  rich: (key: K, tags: RichTags, params?: MessageParams) => ReactNode;
}

export function renderRich(text: string, tags: RichTags): ReactNode {
  const out: ReactNode[] = [];
  const re = /<(\w+)>([\s\S]*?)<\/\1>|<(\w+)\s*\/>/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const name = m[1] ?? m[3]!;
    const fn = tags[name];
    out.push(<Fragment key={i++}>{fn ? fn(m[2] ?? '') : (m[2] ?? '')}</Fragment>);
    last = re.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return <>{out}</>;
}

/** 非 React 场景：直接取某命名空间的翻译函数（取值时读取当前语言） */
export function nsT<M extends object>(ns: string): TFunction<Extract<keyof M, string>> {
  const t = ((key: string, params?: MessageParams) => translate(ns, key, params)) as unknown as TFunction<Extract<keyof M, string>>;
  t.rich = (key, tags, params) => renderRich(translate(ns, key, params), tags);
  return t;
}

/**
 * 组件里取翻译：const t = useT<typeof import('./messages/console/zh-CN').default>('console')
 * 推荐在命名空间目录写 index.ts 导出 useConsoleT 等别名。
 */
export function useT<M extends object>(ns: string): TFunction<Extract<keyof M, string>> {
  const locale = useLocale();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => nsT<M>(ns), [ns, locale]);
}
